import { CustomerCommunication } from './CustomerCommunication.js';
import { CustomerWebPanel } from './CustomerWebPanel.js';
import { Fragment, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../lib/api.js';
import type { Me, Rights } from '../lib/session.js';
import {
  MESSAGE_STATUS_LABEL,
  OPEN_AUTO_LABEL,
  OPEN_HUMAN_LABEL,
  OUTCOME_LABEL,
  REACTION_LABEL,
  REASON_LABEL,
  TIMELINE_LABEL,
  TRIP_STATE_LABEL,
  TICKET_STATUS_LABEL,
  CLOSED_REASON_LABEL,
  CARE_KIND_LABEL,
  SATISFACTION_LABEL,
  label,
} from '../lib/labels.js';
import { date, dateTime, money, num } from '../lib/format.js';
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  PageHeader,
  Panel,
} from '../components/ui.js';
import { ToneBadge } from '../components/2a-badge.js';
import '../screens-2a.css';
import { hrefFor } from '../lib/router.js';
import { BrandSelect, useBrands } from '../lib/brands.js';

interface CustomerRow {
  id: string;
  customer_id: string | null;
  email: string;
  language: string | null;
  country: string | null;
  holdout: boolean;
  hard_bounced_at: string | null;
  source: string | null;
  provider_blacklisted_at: string | null;
  provider_unsubscribed_at: string | null;
  active_trips: number;
  orders: number;
  sources?: string[];
  unsub_scopes?: string[];
  activity_tier?: string | null;
  last_activity_at?: string | null;
}

/** Rozsah odhlášení ze SE (D-328): `race:f1-monza` = jen tento závod, `list:12` = jen tato kampaň. */
export const scopeText = (scopes: string[] | undefined): string =>
  (scopes ?? [])
    .map((x) => (x.startsWith('race:') ? `závod ${x.slice(5)}` : `kampaň ${x.slice(5)}`))
    .join(', ');

const ACTIVITY_LABEL: Record<string, string> = {
  active: 'aktivní',
  slowing: 'zvolna',
  sleeping: 'spící',
  dead: 'mrtvý',
};
const SOURCE_LABEL: Record<string, string> = {
  eshop: 'E-shop',
  smartemailing: 'SmartEmailing',
  event: 'Událost',
  web: 'Web',
  import: 'Import',
  manual: 'Ručně',
};

interface TimelineRow {
  kind: string;
  at: string;
  ref: string;
  data: Record<string, unknown>;
}

interface CustomerCard {
  contact: {
    id: string;
    customer_id: string | null;
    email: string;
    language: string | null;
    country: string | null;
    timezone: string | null;
    language_source: string | null;
    timezone_source: string | null;
    phone: string | null;
    first_name: string | null;
    street?: string | null;
    city?: string | null;
    postal_code?: string | null;
    sources?: string[];
    unsub_scopes?: string[];
    se_created_at?: string | null;
    activity_tier?: string | null;
    last_activity_at?: string | null;
    last_name: string | null;
    company: string | null;
    profile_source: Record<string, string> | null;
    holdout: boolean;
    hard_bounced_at: string | null;
    source?: string | null;
    provider_blacklisted_at?: string | null;
    provider_unsubscribed_at?: string | null;
    created_at: string;
  };
  consents: Array<{
    controller: string;
    purpose: string;
    state: string;
    source: string | null;
    changed_at: string;
  }>;
  consentHistory: Array<{
    controller: string;
    purpose: string;
    old_state: string | null;
    new_state: string;
    at: string;
  }>;
  trips: Array<{
    trip_id: string;
    race_id: string;
    race_name: string;
    seller: string;
    state: string;
    closed_reason: string | null;
    lead_days: number | null;
    ticket_state: string | null;
    flexi_state: string;
    date_start: string | null;
    timezone: string;
  }>;
  orders: Array<{
    order_code: string;
    seller: string;
    status: string;
    paid_at: string | null;
    total: string | null;
    currency: string | null;
    items: Array<{ itemId: string; type: string; raceId: string | null; qty: number }>;
  }>;
  flags: Array<{
    flag: string;
    value: string;
    synced_value: string | null;
    synced_at: string | null;
  }>;
  timeline: TimelineRow[];
  interests: Array<{
    brand: string;
    kind: string;
    key: string;
    score: number;
    reads: number;
    last_at: string;
  }>;
  devices: Array<{
    brand: string;
    device_id: string;
    platform: string | null;
    app_version: string | null;
    push_enabled: boolean;
    last_seen_at: string;
  }>;
  signals: Array<{ kind: string; value: string; source: string; observed_at: string }>;
  providerProfiles: SeProfileRow[];
  rawEvents: Array<{
    event_id: string;
    source: string;
    type: string;
    brand: string | null;
    occurred_at: string;
    received_at: string;
    status: string;
  }>;
}

const FLEXI_LABEL: Record<string, string> = {
  active: 'Aktivní',
  claim_in_progress: 'Žádost běží',
  used: 'Využité',
};

const INTEREST_KIND: Record<string, string> = { series: 'řada', race: 'závod', topic: 'téma' };

const CONSENT_STATE: Record<string, string> = {
  granted: 'uděleno',
  not_given: 'neuděleno',
  withdrawn: 'odvoláno',
};

/** Hodnota parametru adresy, která místo karty zapne filtr „ze SmartEmailingu“. */
export const SOURCE_PARAM = 'zdroj-smartemailing';

export function Customers({
  me,
  rights,
  param,
}: {
  me: Me;
  rights: Rights;
  param?: string;
}): JSX.Element {
  const [query, setQuery] = useState('');
  const [sourceState, setSourceState] = useState('');
  const [activity, setActivity] = useState('');
  /* Odkaz z importu (`#/zakaznici/zdroj-smartemailing`) je filtr podle zdroje, ne karta. */
  const filterOnly = param === SOURCE_PARAM;
  const source = filterOnly ? 'smartemailing' : sourceState;
  const list = useQuery<{ items: CustomerRow[]; total: number }>({
    queryKey: ['customers', query, source, activity],
    queryFn: () => api.get(`/api/customers${qs({ query, source, activity })}`),
    enabled: !param || filterOnly,
  });

  if (param && !filterOnly) return <CustomerCardScreen contactId={param} me={me} rights={rights} />;

  return (
    <>
      <PageHeader
        title="Zákazníci"
        description="Hledejte podle e-mailu, čísla zákazníka nebo kódu objednávky. Karta zákazníka ukáže vše, co o něm hub ví: cesty, souhlasy, odeslané zprávy i ty neodeslané s důvodem. E-maily jsou ve výpisech maskované."
      />

      <div className="s2a-filters">
        <Field label="Hledat zákazníka">
          <input
            type="search"
            value={query}
            placeholder="E-mail, číslo zákazníka, kód objednávky"
            className="s2a-search"
            onChange={(e) => setQuery(e.target.value)}
          />
        </Field>
        <Field label="Zdroj kontaktu">
          <select
            value={source}
            onChange={(e) => {
              setSourceState(e.target.value);
              if (filterOnly) window.location.hash = hrefFor('customers').slice(1);
            }}
          >
            <option value="">Všechny zdroje</option>
            <option value="smartemailing">Importované ze SmartEmailingu</option>
            <option value="eshop">E-shop</option>
            <option value="event">Události (web, aplikace)</option>
            <option value="import">Ruční import</option>
          </select>
        </Field>
        <Field label="Aktivita">
          <select value={activity} onChange={(e) => setActivity(e.target.value)}>
            <option value="">Všechny</option>
            {Object.entries(ACTIVITY_LABEL).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </Field>
        {list.data ? <p className="muted">Nalezeno {num(list.data.total)} kontaktů.</p> : null}
      </div>

      {list.isLoading ? <LoadingState what="zákazníky" /> : null}
      {list.isError ? (
        <ErrorState
          title="Zákazníky se nepodařilo načíst"
          error={list.error}
          onRetry={() => void list.refetch()}
        />
      ) : null}

      <Panel>
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th>E-mail</th>
                <th>Číslo zákazníka</th>
                <th>Jazyk</th>
                <th className="n">Aktivní cesty</th>
                <th className="n">Objednávky</th>
                <th>Zdroje</th>
                <th>Aktivita</th>
                <th>Stav</th>
              </tr>
            </thead>
            <tbody>
              {list.data?.items.map((c) => (
                <tr key={c.id}>
                  <td>
                    <a href={hrefFor('customers', c.id)}>{c.email}</a>
                  </td>
                  <td>
                    <code>{c.customer_id ?? '—'}</code>
                  </td>
                  <td>{c.language ?? '—'}</td>
                  <td className="n">{num(c.active_trips)}</td>
                  <td className="n">{num(c.orders)}</td>
                  <td>
                    {(c.sources ?? []).length === 0
                      ? '—'
                      : (c.sources ?? []).map((x) => (
                          <ToneBadge key={x} tone={x === 'smartemailing' ? 'acc' : 'neu'}>
                            {SOURCE_LABEL[x] ?? x}
                          </ToneBadge>
                        ))}
                  </td>
                  <td>
                    {c.activity_tier ? (
                      <span
                        title={
                          c.last_activity_at
                            ? `Poslední reakce ${dateTime(c.last_activity_at)}`
                            : 'Bez známé reakce'
                        }
                      >
                        <ToneBadge
                          tone={
                            c.activity_tier === 'active'
                              ? 'ok'
                              : c.activity_tier === 'dead'
                                ? 'bad'
                                : 'warn'
                          }
                        >
                          {ACTIVITY_LABEL[c.activity_tier] ?? c.activity_tier}
                        </ToneBadge>
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    {c.provider_blacklisted_at ? (
                      <ToneBadge tone="bad">blokovaný ve SE</ToneBadge>
                    ) : null}
                    {c.provider_unsubscribed_at ? (
                      <span title="Marketing se neposílá, dokud je odhlášený ve SmartEmailingu">
                        <ToneBadge tone="warn">odhlášen ve SE (všechen marketing)</ToneBadge>
                      </span>
                    ) : null}
                    {!c.provider_unsubscribed_at && (c.unsub_scopes ?? []).length > 0 ? (
                      <span title="Globální odhlášení má přednost; tohle platí jen pro uvedený rozsah">
                        <ToneBadge tone="neu">
                          odhlášen jen z: {scopeText(c.unsub_scopes)}
                        </ToneBadge>
                      </span>
                    ) : null}
                    {c.hard_bounced_at ? <ToneBadge tone="bad">nedoručitelný</ToneBadge> : null}
                    {c.holdout ? <ToneBadge tone="sim">kontrolní skupina</ToneBadge> : null}
                    {!c.hard_bounced_at &&
                    !c.holdout &&
                    !c.provider_blacklisted_at &&
                    !c.provider_unsubscribed_at ? (
                      <ToneBadge tone="ok">v pořádku</ToneBadge>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {list.data && list.data.items.length === 0 ? (
          <EmptyState title="Nikdo neodpovídá hledání">
            Zkuste kratší text, nebo hledejte jinak: podle e-mailu, čísla zákazníka nebo kódu
            objednávky.
          </EmptyState>
        ) : null}
      </Panel>
    </>
  );
}

function CustomerCardScreen({
  contactId,
  me,
  rights,
}: {
  contactId: string;
  me: Me;
  rights: Rights;
}): JSX.Element {
  const client = useQueryClient();
  const [brand, setBrand] = useState('');
  const card = useQuery<CustomerCard>({
    queryKey: ['customer', contactId, brand],
    queryFn: () => api.get(`/api/customers/${contactId}${qs({ brand })}`),
  });
  const [error, setError] = useState<string | null>(null);
  const [askDelete, setAskDelete] = useState(false);

  const recompute = useMutation({
    mutationFn: () => api.post(`/api/contacts/${contactId}/recompute`),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['customer', contactId] }),
    onError: (err: Error) => setError(err.message),
  });
  const remove = useMutation({
    mutationFn: (confirmation: string) =>
      api.post(`/api/customers/${contactId}/delete`, { confirmation }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['customer', contactId] }),
    onError: (err: Error) => setError(err.message),
  });

  if (card.isLoading) return <LoadingState what="kartu zákazníka" />;
  if (card.isError)
    return (
      <ErrorState
        title="Kartu zákazníka se nepodařilo načíst"
        error={card.error}
        onRetry={() => void card.refetch()}
      />
    );
  const data = card.data;
  if (!data) return <LoadingState />;

  return (
    <>
      <a href={hrefFor('customers')} className="note">
        ← Zákazníci
      </a>
      <PageHeader
        title={data.contact.email}
        description={
          <>
            Číslo zákazníka <code>{data.contact.customer_id ?? 'chybí'}</code>, jazyk{' '}
            {data.contact.language ?? 'neznámý'}, časové pásmo{' '}
            {data.contact.timezone ?? me.defaultTimezone}.
          </>
        }
        actions={
          <>
            <button className="btn" onClick={() => recompute.mutate()}>
              Vynutit přepočet
            </button>
            {rights.can('delete_contact') ? (
              <button className="btn dng" onClick={() => setAskDelete(true)}>
                Vymazat kontakt
              </button>
            ) : null}
          </>
        }
      />

      {error ? <ErrorState title="Akce se nepovedla" error={new Error(error)} /> : null}

      <div className="grid2">
        <Panel title="Profil">
          <dl className="kv">
            <dt>Zákazníkem od</dt>
            <dd>{date(data.contact.created_at, me.defaultTimezone)}</dd>
            {(['first_name', 'last_name', 'company', 'phone'] as const).map((k) => (
              <Fragment key={k}>
                <dt>{CONTACT_FIELD[k]}</dt>
                <dd>
                  {data.contact[k] ?? '—'}
                  {data.contact.profile_source?.[k] === 'smartemailing' ? (
                    <span className="muted"> · doplněno ze SmartEmailingu</span>
                  ) : null}
                </dd>
              </Fragment>
            ))}
            <dt>Adresa</dt>
            <dd>
              {[data.contact.street, data.contact.postal_code, data.contact.city]
                .filter(Boolean)
                .join(', ') || '—'}
            </dd>
            <dt>Odhlášení ze SE</dt>
            <dd>
              {data.contact.provider_unsubscribed_at
                ? 'všechen marketing (globální odhlášení, má přednost)'
                : (data.contact.unsub_scopes ?? []).length > 0
                  ? `jen ${scopeText(data.contact.unsub_scopes)}`
                  : '—'}
            </dd>
            <dt>Zdroje kontaktu</dt>
            <dd>
              {(data.contact.sources ?? []).map((x) => SOURCE_LABEL[x] ?? x).join(', ') || '—'}
              {data.contact.se_created_at ? (
                <span className="muted">
                  {' '}
                  · ve SmartEmailingu od {date(data.contact.se_created_at)}
                </span>
              ) : null}
            </dd>
            <dt>Aktivita</dt>
            <dd>
              {data.contact.activity_tier
                ? (ACTIVITY_LABEL[data.contact.activity_tier] ?? data.contact.activity_tier)
                : '—'}
              {data.contact.last_activity_at ? (
                <span className="muted">
                  {' '}
                  · poslední reakce {dateTime(data.contact.last_activity_at)}
                </span>
              ) : null}
            </dd>
            <dt>Jazyk</dt>
            <dd>
              {data.contact.language ?? '—'}
              {data.contact.language_source ? (
                <span className="muted"> · zdroj {data.contact.language_source}</span>
              ) : null}
            </dd>
            <dt>Časové pásmo</dt>
            <dd>
              {data.contact.timezone ?? '—'}
              {data.contact.timezone_source ? (
                <span className="muted"> · zdroj {data.contact.timezone_source}</span>
              ) : null}
            </dd>
            <dt>Země</dt>
            <dd>{data.contact.country ?? '—'}</dd>
            <dt>Kontrolní skupina</dt>
            <dd>{data.contact.holdout ? <ToneBadge tone="sim">ano</ToneBadge> : 'ne'}</dd>
            <dt>Zdroj kontaktu</dt>
            <dd>
              {data.contact.source === 'smartemailing'
                ? 'založen importem ze SmartEmailingu'
                : 'e-shop'}
              {data.contact.source === 'smartemailing' && data.contact.customer_id
                ? ' · spárován s e-shopem'
                : ''}
            </dd>
            <dt>Odhlášen ve SmartEmailingu</dt>
            <dd>
              {data.contact.provider_unsubscribed_at ? (
                <>
                  <ToneBadge tone="warn">
                    {dateTime(data.contact.provider_unsubscribed_at, me.defaultTimezone)}
                  </ToneBadge>
                  <span className="muted">
                    {' '}
                    · marketing se neposílá, ani když e-shop souhlas dal
                  </span>
                </>
              ) : (
                'ne'
              )}
            </dd>
            <dt>Blokován ve SmartEmailingu</dt>
            <dd>
              {data.contact.provider_blacklisted_at ? (
                <ToneBadge tone="bad">
                  {dateTime(data.contact.provider_blacklisted_at, me.defaultTimezone)}
                </ToneBadge>
              ) : (
                'ne'
              )}
            </dd>
            <dt>Trvale nedoručitelná adresa</dt>
            <dd>
              {data.contact.hard_bounced_at ? (
                <ToneBadge tone="bad">
                  {dateTime(data.contact.hard_bounced_at, me.defaultTimezone)}
                </ToneBadge>
              ) : (
                'ne'
              )}
            </dd>
          </dl>
        </Panel>

        <CarePanel contactId={contactId} me={me} canManage={rights.can('manage_care')} />

        <Panel title="Souhlasy">
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Správce</th>
                  <th>Účel</th>
                  <th>Stav</th>
                  <th>Změněno</th>
                </tr>
              </thead>
              <tbody>
                {data.consents.map((c) => (
                  <tr key={`${c.controller}-${c.purpose}`}>
                    <td>{c.controller}</td>
                    <td>{c.purpose === 'watchdog' ? 'sledování' : 'marketing'}</td>
                    <td>
                      {c.state === 'granted' ? (
                        <ToneBadge tone="ok">uděleno</ToneBadge>
                      ) : c.state === 'withdrawn' ? (
                        <ToneBadge tone="bad">odvoláno</ToneBadge>
                      ) : (
                        <ToneBadge tone="neu">neuděleno</ToneBadge>
                      )}
                    </td>
                    <td>{dateTime(c.changed_at, me.defaultTimezone)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.consentHistory.length > 0 ? (
            <details className="s2a-mt">
              <summary className="note">Historie souhlasů</summary>
              <ul className="note">
                {data.consentHistory.map((h, i) => (
                  <li key={i}>
                    {dateTime(h.at, me.defaultTimezone)} · {h.controller} / {h.purpose}:{' '}
                    {label(CONSENT_STATE, h.old_state ?? 'neuvedeno')} →{' '}
                    {label(CONSENT_STATE, h.new_state)}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </Panel>
      </div>

      <Panel title="Cesty">
        {data.trips.length === 0 ? (
          <EmptyState title="Kontakt nemá žádnou cestu">
            Cesta vznikne z první objednávky na závod.
          </EmptyState>
        ) : (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Závod</th>
                  <th>Prodejce</th>
                  <th>Stav</th>
                  <th className="n">Předstih</th>
                  <th>Vstupenky</th>
                  <th>Flexi</th>
                </tr>
              </thead>
              <tbody>
                {data.trips.map((t) => (
                  <tr key={t.trip_id}>
                    <td>
                      <a href={hrefFor('races', t.race_id)}>{t.race_name}</a>
                      <div className="muted">{date(t.date_start, t.timezone)}</div>
                    </td>
                    <td>{t.seller}</td>
                    <td>
                      {label(TRIP_STATE_LABEL, t.state)}
                      {t.closed_reason ? (
                        <div className="muted">{label(CLOSED_REASON_LABEL, t.closed_reason)}</div>
                      ) : null}
                    </td>
                    <td className="n">{t.lead_days === null ? '—' : `${t.lead_days} d`}</td>
                    <td>{t.ticket_state ? label(TICKET_STATUS_LABEL, t.ticket_state) : '—'}</td>
                    <td>{t.flexi_state === 'none' ? '—' : label(FLEXI_LABEL, t.flexi_state)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel title="Objednávky">
        {data.orders.length === 0 ? (
          <EmptyState title="Kontakt nemá objednávky">
            Objednávky se sem dostanou z e-shopu.
          </EmptyState>
        ) : (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Kód</th>
                  <th>Prodejce</th>
                  <th>Stav</th>
                  <th>Zaplaceno</th>
                  <th className="n">Celkem</th>
                  <th>Položky</th>
                </tr>
              </thead>
              <tbody>
                {data.orders.map((o) => (
                  <tr key={o.order_code}>
                    <td>
                      <code>{o.order_code}</code>
                    </td>
                    <td>{o.seller}</td>
                    <td>{o.status}</td>
                    <td>{date(o.paid_at, me.defaultTimezone)}</td>
                    <td className="n">{money(o.total, o.currency ?? 'EUR')}</td>
                    <td className="muted">
                      {o.items
                        .map((i) => `${i.type}${i.raceId ? ` (${i.raceId})` : ''}`)
                        .join(', ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <CustomerCommunication contactId={contactId} me={me} />

      <Panel title="Časová osa" action={<BrandSelect value={brand} onChange={setBrand} />}>
        <p className="note">
          Objednávky, zprávy včetně zalogovaných z e-shopu, reakce na zprávy (i z ručních kampaní ve
          SmartEmailingu), neodeslané zprávy s důvodem, signály jazyka a pásma, čtení článků,
          události z webu a z aplikace a kontakty s podporou. Filtr značky ponechá i záznamy bez
          značky.
        </p>
        <ul className="timeline cardlog" data-testid="timeline">
          {data.timeline.map((row, i) => (
            <TimelineItem key={i} row={row} me={me} />
          ))}
        </ul>
        {data.timeline.length === 0 ? (
          <EmptyState title="Na kartě zatím nic není">
            Záznamy přibudou s objednávkami, zprávami a návštěvami webu.
          </EmptyState>
        ) : null}
      </Panel>

      <CustomerWebPanel contactId={contactId} />

      <div className="grid2">
        <Panel title="Zájmy">
          {data.interests.length === 0 ? (
            <EmptyState title="Zatím nic nenasbíráno">
              Zájmy vznikají čtením článků na webu.
            </EmptyState>
          ) : (
            <div className="scroll">
              <table>
                <thead>
                  <tr>
                    <th>Druh</th>
                    <th>Klíč</th>
                    <th className="n">Skóre</th>
                    <th className="n">Přečteno</th>
                  </tr>
                </thead>
                <tbody>
                  {data.interests.map((i) => (
                    <tr key={`${i.brand}-${i.kind}-${i.key}`}>
                      <td>{label(INTEREST_KIND, i.kind)}</td>
                      <td>
                        <code>{i.key}</code>
                      </td>
                      <td className="n">{i.score.toFixed(2)}</td>
                      <td className="n">{num(i.reads)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Panel title="Zařízení">
          {data.devices.length === 0 ? (
            <EmptyState title="Žádné zařízení s aplikací">
              Zařízení se objeví, až se zákazník přihlásí v mobilní aplikaci.
            </EmptyState>
          ) : (
            <div className="scroll">
              <table>
                <thead>
                  <tr>
                    <th>Zařízení</th>
                    <th>Značka</th>
                    <th>Aplikace</th>
                    <th>Push</th>
                    <th>Naposledy</th>
                  </tr>
                </thead>
                <tbody>
                  {data.devices.map((d) => (
                    <tr key={`${d.brand}-${d.device_id}`}>
                      <td>
                        <code>{d.device_id}</code>
                        <div className="muted">{d.platform ?? '—'}</div>
                      </td>
                      <td>{d.brand}</td>
                      <td>{d.app_version ?? '—'}</td>
                      <td>
                        {d.push_enabled ? (
                          <ToneBadge tone="ok">zapnutý</ToneBadge>
                        ) : (
                          <ToneBadge tone="neu">vypnutý</ToneBadge>
                        )}
                      </td>
                      <td>{dateTime(d.last_seen_at, me.defaultTimezone)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>

      <SeProfilePanel rows={data.providerProfiles ?? []} me={me} />

      <RawEvents contactId={contactId} items={data.rawEvents} me={me} />

      {data.flags.length > 0 ? (
        <Panel title="Příznaky">
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Příznak</th>
                  <th>Hodnota</th>
                  <th>Synchronizováno</th>
                </tr>
              </thead>
              <tbody>
                {data.flags.map((f) => (
                  <tr key={f.flag}>
                    <td>
                      <code>{f.flag}</code>
                    </td>
                    <td>{f.value}</td>
                    <td>
                      {f.synced_value === f.value ? (
                        <ToneBadge tone="ok">ano</ToneBadge>
                      ) : (
                        <ToneBadge tone="warn">čeká</ToneBadge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      ) : null}

      <ConfirmDialog
        open={askDelete}
        title="Vymazat kontakt?"
        danger
        requireWord="VYMAZAT"
        confirmLabel="Vymazat"
        busy={remove.isPending}
        onCancel={() => setAskDelete(false)}
        onConfirm={(word) => {
          remove.mutate(word ?? '');
          setAskDelete(false);
        }}
      >
        Výmaz anonymizuje kontakt v hubu a zařadí výmaz ve SmartEmailingu. Nejde vzít zpět.
      </ConfirmDialog>
    </>
  );
}

interface SeProfileRow {
  brand: string;
  seContactId: number;
  syncedAt: string;
  blacklisted: boolean;
  hardbounced: boolean;
  purposes: Array<{ id: number; name: string }>;
  profile: {
    [key: string]: unknown;
    lists?: Array<{ listId: number; name: string | null; status: string; added: string | null }>;
    customFields?: Array<{
      fieldId: number | null;
      name: string;
      type: string | null;
      values: string[];
    }>;
  } | null;
}

const SE_STATUS: Record<string, string> = {
  confirmed: 'potvrzený',
  unconfirmed: 'nepotvrzený',
  unsubscribed: 'odhlášený',
  removed: 'odebraný',
};
const SE_FIELDS: Array<[string, string]> = [
  ['titlesBefore', 'Titul před'],
  ['name', 'Jméno'],
  ['surname', 'Příjmení'],
  ['titlesAfter', 'Titul za'],
  ['salutation', 'Oslovení'],
  ['gender', 'Pohlaví'],
  ['company', 'Firma'],
  ['cellphone', 'Mobil'],
  ['phone', 'Telefon'],
  ['street', 'Ulice'],
  ['town', 'Město'],
  ['postalCode', 'PSČ'],
  ['country', 'Země'],
  ['language', 'Jazyk'],
  ['birthday', 'Narozeniny'],
  ['nameday', 'Svátek'],
  ['notes', 'Poznámky'],
  ['created', 'Vytvořen ve SE'],
  ['updated', 'Změněn ve SE'],
  ['lastOpened', 'Naposledy otevřel'],
  ['lastClicked', 'Naposledy klikl'],
];

/** Zrcadlo kontaktu ze SmartEmailingu (jen čtení, D-101); filtr značky řeší server. */

function CarePanel({
  contactId,
  me,
  canManage,
}: {
  contactId: string;
  me: Me;
  canManage: boolean;
}): JSX.Element {
  const client = useQueryClient();
  const q = useQuery<{
    canSeeNotes: boolean;
    flags: Array<{
      id: number;
      brand: string;
      race_name: string | null;
      kind: string;
      note: string | null;
      set_at: string;
      active: boolean;
    }>;
    cases: Array<{
      id: number;
      topic: string | null;
      status: string;
      satisfaction: string;
      opened_at: string;
      resolution: string | null;
    }>;
  }>({
    queryKey: ['customer-care', contactId],
    queryFn: () => api.get(`/api/customers/${contactId}/care`),
  });
  const brands = (useBrands().data?.items ?? []).filter((b) => b.kind === 'shop');
  const [form, setForm] = useState({ brand: '', kind: 'problem', note: '' });
  const brand = form.brand || brands[0]?.code || '';
  const add = useMutation({
    mutationFn: () =>
      api.post('/api/care/flags', {
        contactId,
        brand,
        kind: form.kind,
        note: form.note || undefined,
      }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['customer-care', contactId] }),
  });
  if (!q.data)
    return (
      <Panel title="Péče">
        <LoadingState what="péči" />
      </Panel>
    );
  return (
    <Panel
      title="Péče"
      subtitle="Označení drží marketingové zprávy k závodu; případy ukazují, jak jsme zákazníka obsloužili."
    >
      {q.data.flags.length === 0 ? (
        <p className="note">
          Zákazník není označen, marketing k závodům mu odchází běžným způsobem.
        </p>
      ) : (
        <ul className="plain">
          {q.data.flags.map((f) => (
            <li key={f.id}>
              <ToneBadge tone={f.active ? 'warn' : 'neu'}>
                {label(CARE_KIND_LABEL, f.kind)}
              </ToneBadge>{' '}
              {f.brand} · {f.race_name ?? 'všechny závody'} ·{' '}
              {dateTime(f.set_at, me.defaultTimezone)}
              {f.active ? '' : ' (zrušeno)'}
              {f.note ? ` — ${f.note}` : ''}
            </li>
          ))}
        </ul>
      )}
      <ul className="plain">
        {q.data.cases.map((c) => (
          <li key={c.id}>
            {dateTime(c.opened_at, me.defaultTimezone)} · {c.topic ?? 'případ'} ·{' '}
            {c.status === 'open' ? 'otevřený' : 'vyřešený'} ·{' '}
            {label(SATISFACTION_LABEL, c.satisfaction)}
            {c.resolution ? ` — ${c.resolution}` : ''}
          </li>
        ))}
      </ul>
      {canManage ? (
        <div className="s2a-form">
          <Field label="Značka označení">
            <select value={brand} onChange={(e) => setForm({ ...form, brand: e.target.value })}>
              {brands.map((b) => (
                <option key={b.code} value={b.code}>
                  {b.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Druh označení">
            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
              {Object.entries(CARE_KIND_LABEL).map(([v, t]) => (
                <option key={v} value={v}>
                  {t}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Poznámka k označení">
            <input
              type="text"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
            />
          </Field>
          <button className="btn" onClick={() => add.mutate()}>
            Označit zákazníka (všechny závody značky)
          </button>
        </div>
      ) : null}
    </Panel>
  );
}

function SeProfilePanel({ rows, me }: { rows: SeProfileRow[]; me: Me }): JSX.Element {
  return (
    <Panel title="Data ze SmartEmailingu">
      {rows.length === 0 ? (
        <EmptyState title="Data ze SmartEmailingu zatím nejsou">
          Zobrazí se po importu ze SmartEmailingu (Nastavení → Import ze SmartEmailingu), pokud je
          kontakt ve SmartEmailingu.
        </EmptyState>
      ) : (
        rows.map((r) => {
          const p = r.profile;
          return (
            <div key={`${r.brand}-${r.seContactId}`}>
              <h3>
                {r.brand}{' '}
                <span className="muted">
                  · ID ve SmartEmailingu {r.seContactId} · naposledy synchronizováno{' '}
                  {dateTime(r.syncedAt, me.defaultTimezone)}
                </span>
              </h3>
              <p>
                {r.blacklisted ? (
                  <ToneBadge tone="bad">blokovaný ve SE</ToneBadge>
                ) : (
                  <ToneBadge tone="ok">neblokovaný</ToneBadge>
                )}{' '}
                {r.hardbounced ? <ToneBadge tone="bad">trvale nedoručitelný</ToneBadge> : null}
              </p>
              {p === null ? (
                <p className="note">Starší zrcadlo bez plného profilu; spusťte import znovu.</p>
              ) : (
                <>
                  <dl className="kv">
                    {SE_FIELDS.filter(([k]) => p[k] !== undefined && p[k] !== null).map(
                      ([k, l]) => (
                        <Fragment key={k}>
                          <dt>{l}</dt>
                          <dd>{String(p[k])}</dd>
                        </Fragment>
                      ),
                    )}
                  </dl>
                  <h4>Seznamy</h4>
                  {p.lists?.length ? (
                    <div className="scroll">
                      <table>
                        <thead>
                          <tr>
                            <th>Seznam</th>
                            <th>Stav</th>
                            <th>Přidán</th>
                          </tr>
                        </thead>
                        <tbody>
                          {p.lists.map((l) => (
                            <tr key={l.listId}>
                              <td>{l.name ?? `#${l.listId}`}</td>
                              <td>
                                <ToneBadge
                                  tone={
                                    l.status === 'confirmed'
                                      ? 'ok'
                                      : l.status === 'unsubscribed'
                                        ? 'bad'
                                        : 'warn'
                                  }
                                >
                                  {SE_STATUS[l.status] ?? l.status}
                                </ToneBadge>
                              </td>
                              <td>{l.added ?? '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="note">Kontakt není v žádném seznamu.</p>
                  )}
                  <h4>Účely (platný souhlas)</h4>
                  <p>{r.purposes.length ? r.purposes.map((x) => x.name).join(', ') : '—'}</p>
                  <h4>Vlastní pole</h4>
                  {p.customFields?.length ? (
                    <div className="scroll">
                      <table>
                        <thead>
                          <tr>
                            <th>Pole</th>
                            <th>Typ</th>
                            <th>Hodnota</th>
                          </tr>
                        </thead>
                        <tbody>
                          {p.customFields.map((f) => (
                            <tr key={`${f.fieldId ?? f.name}`}>
                              <td>{f.name}</td>
                              <td>{f.type ?? '—'}</td>
                              <td>{f.values.join(', ')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="note">Žádná vyplněná vlastní pole.</p>
                  )}
                </>
              )}
            </div>
          );
        })
      )}
    </Panel>
  );
}

const CONTACT_FIELD = {
  first_name: 'Jméno',
  last_name: 'Příjmení',
  company: 'Firma',
  phone: 'Telefon',
} as const;

function RawEvents({
  contactId,
  items,
  me,
}: {
  contactId: string;
  items: CustomerCard['rawEvents'];
  me: Me;
}): JSX.Element {
  const [shown, setShown] = useState(false);
  const raw = useQuery<{ items: Array<{ event_id: string; payload: unknown }> }>({
    queryKey: ['customer-raw', contactId],
    queryFn: () => api.get(`/api/customers/${contactId}/raw-events`),
    enabled: shown,
  });
  const payload = new Map(raw.data?.items.map((r) => [r.event_id, r.payload]));

  return (
    <Panel title="Původní události ze zdrojů">
      <p className="note">
        Technický záznam toho, co zdroj (e-shop, web, aplikace) poslal před zpracováním. Obsah se
        načte až na požádání.
      </p>
      {items.length === 0 ? (
        <EmptyState title="Žádné události">K tomuto kontaktu zatím nic nepřišlo.</EmptyState>
      ) : null}
      {items.length > 0 ? (
        <>
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Typ</th>
                  <th>Zdroj</th>
                  <th>Značka</th>
                  <th>Přijato</th>
                  <th>Stav</th>
                </tr>
              </thead>
              <tbody>
                {items.map((e) => (
                  <tr key={e.event_id}>
                    <td>
                      <code>{e.type}</code>
                      {shown && payload.has(e.event_id) ? (
                        <pre className="note">
                          {JSON.stringify(payload.get(e.event_id), null, 2)}
                        </pre>
                      ) : null}
                    </td>
                    <td>{e.source}</td>
                    <td>{e.brand ?? '—'}</td>
                    <td>{dateTime(e.received_at, me.defaultTimezone)}</td>
                    <td>{e.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!shown ? (
            <button className="btn s2a-mt" onClick={() => setShown(true)}>
              Ukázat obsah
            </button>
          ) : null}
          {raw.isLoading ? <LoadingState what="obsah událostí" /> : null}
          {raw.isError ? (
            <ErrorState
              title="Obsah událostí se nepodařilo načíst"
              error={raw.error}
              onRetry={() => void raw.refetch()}
            />
          ) : null}
        </>
      ) : null}
    </Panel>
  );
}

function TimelineItem({ row, me }: { row: TimelineRow; me: Me }): JSX.Element {
  const data = row.data as Record<string, string | null>;
  // Tridy tecek jsou z prototypu: s = objednavka, m = zprava, x = pozor, o = ostatni.
  let dot = 'o';
  let text: JSX.Element;

  if (row.kind === 'message') {
    const status = data.status ?? '';
    dot = status === 'bounced' || status === 'failed' ? 'x' : 'm';
    text = (
      <span>
        <b>
          <code>{row.ref}</code>
        </b>{' '}
        {label(MESSAGE_STATUS_LABEL, status)}
        {data.sender === 'eshop' ? <span className="muted"> · posílá e-shop</span> : null}
        <div className="muted">
          {data.templateKey}
          {data.delivered ? ' · doručeno' : ''}
          {data.opened ? ` · ${data.openedHuman ? OPEN_HUMAN_LABEL : OPEN_AUTO_LABEL}` : ''}
          {data.clicked ? ' · proklik' : ''}
          {data.unsubscribed ? ' · odhlášení' : ''}
        </div>
      </span>
    );
  } else if (row.kind === 'decision') {
    dot = 'x';
    text = (
      <span>
        <b>
          <code>{row.ref}</code>
        </b>{' '}
        {label(OUTCOME_LABEL, data.outcome ?? '')} —{' '}
        <span className="muted">{label(REASON_LABEL, data.reason ?? '')}</span>
      </span>
    );
  } else if (row.kind === 'order') {
    dot = 's';
    text = (
      <span>
        Objednávka <code>{row.ref}</code> · {data.seller} · {data.status}
      </span>
    );
  } else if (row.kind === 'support') {
    dot = 'x';
    text = <span>Kontakt s podporou {data.topic ? `— ${data.topic}` : ''}</span>;
  } else if (row.kind === 'web_event') {
    const props = (row.data as { props?: { path?: unknown; name?: unknown } | null }).props;
    text =
      row.ref === 'page_view' && typeof props?.path === 'string' ? (
        <span>
          Návštěva stránky <code>{props.path}</code>
        </span>
      ) : row.ref === 'key_event' && typeof props?.name === 'string' ? (
        <span>
          Klíčová akce na webu: <b>{props.name}</b>
        </span>
      ) : (
        <span>Web: {row.ref}</span>
      );
  } else if (row.kind === 'reaction') {
    dot =
      row.ref === 'unsubscribed' || row.ref === 'bounced' || row.ref === 'complained' ? 'x' : 'm';
    text = (
      <span>
        <b>
          {row.ref === 'opened' && data.human !== undefined && data.human !== null
            ? data.human
              ? OPEN_HUMAN_LABEL
              : OPEN_AUTO_LABEL
            : label(REACTION_LABEL, row.ref)}
        </b>
        {data.manual ? (
          <span className="muted">
            {' '}
            · ruční kampaň v SmartEmailingu{data.campaignType ? ` (${data.campaignType})` : ''}
          </span>
        ) : null}
        {data.link ? <div className="muted">{data.link}</div> : null}
        {data.historical ? <span className="muted"> · z importu</span> : null}
      </span>
    );
  } else if (row.kind === 'signal') {
    text = (
      <span>
        {row.ref === 'language' ? 'Jazyk' : 'Časové pásmo'}: <b>{data.value}</b>{' '}
        <span className="muted">· zdroj {data.source}</span>
      </span>
    );
  } else if (row.kind === 'content_read') {
    text = (
      <span>
        Přečetl článek <b>{data.title ?? row.ref}</b>
      </span>
    );
  } else if (row.kind === 'app_event') {
    text = <span>Aplikace: {row.ref === 'app_session' ? 'otevřena' : row.ref}</span>;
  } else {
    text = (
      <span>
        {label(TIMELINE_LABEL, row.kind)}: <code>{row.ref}</code>
      </span>
    );
  }

  return (
    <li>
      <span className="t">{dateTime(row.at, me.defaultTimezone)}</span>
      <span className={`dot ${dot}`} aria-hidden="true" />
      <span className="b">{text}</span>
    </li>
  );
}
